import {
	Suspense,
	lazy,
	useEffect,
	useMemo,
} from "react";
import {
	Navigate,
	Outlet,
	Route,
	Routes,
	useLocation,
} from "react-router-dom";

import { AuthenticationCheck } from "./AuthenticationCheck";
import { AnalysisContextProvider } from "./contexts/analysisContext";
import { LedgerContextProvider } from "./contexts/ledgerContext";
import { LitheContextProvider } from "./contexts/litheContext";
import { SanitizationContextProvider } from "./contexts/sanitizationContext";
import Agent from "./pages/Agent";
import { PasswordSetCheck } from "./PasswordSetCheck";
import {
	useAppDispatch,
	useAppSelector,
} from "./redux/store";
import { TenantActions } from "./redux/tenants/tenant.slice";
import { LoaderContainer } from "./shared/Loader";
import {
	getPreferredActiveTenant,
	getTenantIdFromPathname,
	stripTenantFromPathname,
	toTenantPath,
} from "./utils/tenantUtils";

const Login = lazy(
	async () => await import("./pages/Login")
);
const MultiFactor = lazy(
	async () => await import("./pages/MultiFactor")
);
const SanitizeTape = lazy(
	async () => await import("./pages/SanitizeTape")
);
const CreatePassword = lazy(
	async () => await import("./pages/CreatePassword")
);
const LandingPage = lazy(
	async () => await import("./pages/LandingPage")
);

const LedgerSearch = lazy(
	async () => await import("./pages/LedgerSearch")
);

const AddDatasetPage = lazy(
	async () => await import("./pages/AddDatasetPage")
);

const TapeSearch = lazy(
	async () => await import("./pages/TapeSearch")
);

const LitheSearch = lazy(
	async () => await import("./pages/LitheSearch")
);

const AnalysisPage = lazy(
	async () => await import("./pages/Analysis")
);

const LithePage = lazy(
	async () => await import("./pages/Lithe")
);

const UserManagement = lazy(
	async () => await import("./pages/UserManagement")
);

const HammerSearch = lazy(
	async () => await import("./pages/HammerSearch")
);

const HammerScreen = lazy(
	async () =>
		await import("./components/hammer/HammerScreen")
);

const DatasetFormulae = lazy(
	async () => await import("./pages/DatasetFormulae")
);

const DatasetPaulSync = lazy(
	async () => await import("./pages/DatasetPaulSync")
);

const SentryTestPage = lazy(
	async () => await import("./pages/SentryTestPage")
);

export const AppRouter = () => {
	const dispatch = useAppDispatch();
	const { authStatus, isPasswordSet } = useAppSelector(
		(state) => state.user
	);
	const { activeTenant, allTenants } = useAppSelector(
		(state) => state.tenant
	);
	const location = useLocation();
	const tenantIdFromUrl = getTenantIdFromPathname(
		location.pathname
	);
	const tenantlessPathname = stripTenantFromPathname(
		location.pathname
	);
	const routeLocation = useMemo(
		() =>
			tenantlessPathname === location.pathname
				? location
				: {
						...location,
						pathname: tenantlessPathname,
					},
		[location, tenantlessPathname]
	);
	const allTenantIds = allTenants
		? Object.keys(allTenants)
		: [];
	const fallbackTenant = getPreferredActiveTenant(
		allTenantIds,
		activeTenant
	);
	const preferredTenant = allTenants
		? fallbackTenant
		: activeTenant;
	const isTenantlessPublicPath =
		tenantlessPathname.startsWith("/login") ||
		tenantlessPathname.startsWith("/create-password") ||
		tenantlessPathname.startsWith(
			"/multi-factor-verification"
		);
	const isResetPasswordMode =
		new URLSearchParams(location.search).get("mode") ===
		"resetPassword";

	useEffect(() => {
		if (
			tenantIdFromUrl &&
			tenantIdFromUrl !== activeTenant &&
			(!allTenants || allTenants[tenantIdFromUrl])
		) {
			dispatch(
				TenantActions.setActiveTenantReducer(tenantIdFromUrl)
			);
		}
	}, [
		activeTenant,
		allTenants,
		dispatch,
		tenantIdFromUrl,
	]);

	if (
		isResetPasswordMode &&
		tenantlessPathname === "/"
	) {
		return (
			<Navigate
				to={`/login${location.search}`}
				replace={true}
			/>
		);
	}

	if (
		authStatus.isLoggedIn &&
		preferredTenant &&
		!tenantIdFromUrl &&
		!isTenantlessPublicPath &&
		!isResetPasswordMode
	) {
		return (
			<Navigate
				to={toTenantPath(
					`${location.pathname}${location.search}${location.hash}`,
					preferredTenant
				)}
				replace={true}
			/>
		);
	}

	if (
		authStatus.isLoggedIn &&
		tenantIdFromUrl &&
		allTenants &&
		!allTenants[tenantIdFromUrl] &&
		fallbackTenant &&
		!isTenantlessPublicPath
	) {
		return (
			<Navigate
				to={toTenantPath(
					`${tenantlessPathname}${location.search}${location.hash}`,
					fallbackTenant
				)}
				replace={true}
			/>
		);
	}

	return (
		<Routes location={routeLocation}>
			<Route
				path="/"
				element={<AuthenticationCheck />}
			>
				<Route
					path="/"
					element={<PasswordSetCheck />}
				>
					<Route
						path="/users"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<UserManagement />
							</Suspense>
						}
					/>
					<Route
						path="/analysis/"
						element={<Outlet />}
					>
						<Route
							path=":tapeId"
							element={
								<Suspense
									fallback={<LoaderContainer width={50} />}
								>
									<AnalysisContextProvider key={tenantlessPathname}>
										<AnalysisPage />
									</AnalysisContextProvider>
								</Suspense>
							}
						/>
						<Route
							path=":tapeId/structures"
							element={
								<Suspense
									fallback={<LoaderContainer width={50} />}
								>
									<HammerSearch />
								</Suspense>
							}
						/>
						<Route
							path=":tapeId/structures/:structureId"
							element={
								<Suspense
									fallback={<LoaderContainer width={50} />}
								>
									<HammerScreen />
								</Suspense>
							}
						/>
					</Route>
					<Route
						path="/ledger"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<LedgerSearch />
							</Suspense>
						}
					/>
					<Route
						path="add-dataset/:datasetId/*"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<LedgerContextProvider>
									<AddDatasetPage />
								</LedgerContextProvider>
							</Suspense>
						}
					/>
					<Route
						path="/library"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<TapeSearch />
							</Suspense>
						}
					/>
					<Route
						path="/lithe-library"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<LitheSearch />
							</Suspense>
						}
					/>
					<Route
						path="/lithe/:method/:step/:litheId"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<LitheContextProvider>
									<LithePage />
								</LitheContextProvider>
							</Suspense>
						}
					/>
					<Route
						path="/sanitize/:step/:tapeId"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<SanitizationContextProvider>
									<SanitizeTape />
								</SanitizationContextProvider>
							</Suspense>
						}
					/>
					<Route
						path="/"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<LandingPage />
							</Suspense>
						}
					/>
					<Route
						path="/dataset-formulae"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<DatasetFormulae />
							</Suspense>
						}
					/>
					<Route
						path="/dataset-paul-sync"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<DatasetPaulSync />
							</Suspense>
						}
					/>
					<Route
						path="/agent"
						element={<Agent />}
					/>

					<Route
						path="/agent/chat/:conversationId"
						element={<Agent />}
					/>

					<Route
						path="/sentry-test"
						element={
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<SentryTestPage />
							</Suspense>
						}
					/>
				</Route>

				<Route
					path="/create-password"
					element={
						isPasswordSet ? (
							<Navigate to={"/"} />
						) : (
							<Suspense
								fallback={<LoaderContainer width={50} />}
							>
								<CreatePassword />
							</Suspense>
						)
					}
				/>

				<Route
					path="/agent"
					element={<Agent />}
				/>
			</Route>

			<Route
				path="/login"
				element={
					authStatus.isLoggedIn &&
					!isResetPasswordMode ? (
						<Navigate to={"/"} />
					) : (
						<Suspense
							fallback={<LoaderContainer width={50} />}
						>
							<Login />
						</Suspense>
					)
				}
			/>

			<Route
				path="/multi-factor-verification"
				element={
					<Suspense
						fallback={<LoaderContainer width={50} />}
					>
						<MultiFactor />
					</Suspense>
				}
			/>

			<Route
				path="*"
				element={<div>404</div>}
			/>
		</Routes>
	);
};
